import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MapPane,
  formatInt,
  formatPct,
  getPrecinctLabel,
  getStatsForOkrsek,
  getSubjectLabel,
  useElectionData,
} from "./Map";
import {
  BRAND_COLORS,
  BRAND_MAP_COLOR_PRESETS,
  normalizeHexColor,
} from "./branding";
import { ELECTION_MAP_STANDARD_V1_KEY, getElectionMapFeatureStyle } from "./mapRenderingStandard";
import { getElectionLabel, sortElectionsNewestFirst } from "./electionOrdering";
import {
  pickValidElectionId,
  readElectionSelectionContext,
  writeElectionSelectionContext,
} from "./electionSelectionContext";
import {
  TSUNAMI_NEUTRAL_FILL,
  TSUNAMI_RANKS,
  TSUNAMI_STORAGE_KEY,
  buildTsunamiEntityColorMap,
  buildTsunamiRankByPrecinct,
  getTsunamiFillColor,
  getTsunamiEntityIdentity,
  getTsunamiEntityName,
  getTsunamiPaletteColors,
  normalizeTsunamiRank,
  sanitizeTsunamiColorOverrides,
} from "./tsunami";
import {
  clearStoredViewport,
  getInitialMapView,
  makeAutoFitRequestKey,
  readStoredViewport,
  writeStoredViewport,
} from "./mapViewport";
import { useAuth } from "./auth";
import AddToReportButton from "./reports/components/AddToReportButton";
import { buildDefaultReportTitle, captureLeafletViewport, mapLegendToSnapshot } from "./reports/capture";
import { REPORT_ITEM_TYPES, REPORT_ORIENTATIONS, REPORT_SOURCE_MODULES } from "./reports/reportTypes";

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

function readStoredTsunamiSettings() {
  if (typeof window === "undefined" || !window.localStorage) return {};
  try {
    const raw = window.localStorage.getItem(TSUNAMI_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function writeStoredTsunamiSettings(settings) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(TSUNAMI_STORAGE_KEY, JSON.stringify(settings && typeof settings === "object" ? settings : {}));
  } catch (_) {
    // Local color overrides are best-effort only.
  }
}

function createHttpError(status, label) {
  const error = new Error(`${label} HTTP ${status}`);
  error.status = Number(status);
  return error;
}

function getFeaturePrecinctId(feature) {
  return String(feature && feature.properties ? feature.properties.okrsek || feature.properties.district_code || "" : "").trim();
}

function getMunicipalityName(properties) {
  const districtName = String(properties?.naz_mco || "").trim();
  const municipalityName = String(properties?.naz_obec || "").trim();
  return districtName || municipalityName || "";
}

function getFeaturePrecinctLabel(feature, electionId) {
  const properties = feature && feature.properties ? feature.properties : {};
  return getPrecinctLabel(
    {
      raw: getFeaturePrecinctId(feature),
      areaCsuCode: properties.area_csu_code,
      municipalityName: getMunicipalityName(properties),
    },
    electionId,
    null
  );
}

function buildStatsByPrecinct(stats) {
  const next = new Map();
  for (const row of Array.isArray(stats) ? stats : []) {
    const precinctId = String(row && row.okrsek ? row.okrsek : "").trim();
    if (precinctId) next.set(precinctId, row);
  }
  return next;
}

function formatTooltipPct(value) {
  return value == null ? "–" : formatPct(value);
}

function TsunamiTooltip({ tooltip, row, subjectLabel, selectedRank }) {
  if (!tooltip || !row) return null;

  const entityName = row.rankEntry?.entityName || "Bez dat pro zvolené pořadí";
  const votes = row.rankEntry?.votes;
  const percentage = row.rankEntry?.percentage;
  const turnout = row.statsInfo?.turnoutPct;

  return (
    <div className="tsunami-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
      <div className="tsunami-tooltip-title">{row.precinctLabel}</div>
      <div className="tsunami-tooltip-row">
        <span>{subjectLabel}</span>
        <strong>{entityName}</strong>
      </div>
      <div className="tsunami-tooltip-grid">
        <span>Pořadí</span>
        <strong>{selectedRank}.</strong>
        <span>Hlasy</span>
        <strong>{votes == null ? "–" : formatInt(votes)}</strong>
        <span>Podíl</span>
        <strong>{formatTooltipPct(percentage)}</strong>
        <span>Účast</span>
        <strong>{formatTooltipPct(turnout)}</strong>
      </div>
    </div>
  );
}

function TsunamiAreaSelect() {
  const { availableAreas, currentAreaId } = useAuth();
  const [error, setError] = useState("");
  const [isSwitching, setIsSwitching] = useState(false);
  const areas = Array.isArray(availableAreas) ? availableAreas : [];

  async function onChange(event) {
    const nextAreaId = Number(event.target.value);
    if (!Number.isInteger(nextAreaId) || nextAreaId <= 0 || nextAreaId === currentAreaId) return;

    setError("");
    setIsSwitching(true);

    try {
      const response = await fetch("/api/auth/switch-area", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ area_id: nextAreaId }),
      });
      const payload = await parseJsonSafe(response);
      if (!response.ok) {
        const message =
          (payload && typeof payload.error === "string" && payload.error) ||
          `Area switch failed (${response.status})`;
        throw new Error(message);
      }
      window.location.assign("/app/tsunami");
    } catch (err) {
      setError(err && err.message ? String(err.message) : "Area switch failed");
      setIsSwitching(false);
    }
  }

  return (
    <label className="tsunami-control-stack">
      <span className="tsunami-label">Oblast</span>
      <select
        className="tsunami-select"
        value={currentAreaId || (areas[0] && areas[0].id) || ""}
        onChange={onChange}
        disabled={isSwitching || areas.length <= 1}
      >
        {areas.map((area) => (
          <option key={area.id} value={area.id}>
            {area.name}
          </option>
        ))}
      </select>
      {isSwitching ? <span className="tsunami-control-note">Přepínám…</span> : null}
      {error ? <span className="tsunami-control-error">{error}</span> : null}
    </label>
  );
}

function getColorPopoverPosition(anchorElement) {
  if (!anchorElement || typeof anchorElement.getBoundingClientRect !== "function") {
    return { top: 96, left: 24 };
  }
  const rect = anchorElement.getBoundingClientRect();
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
  const popoverWidth = 236;
  const left = Math.max(12, Math.min(rect.right - popoverWidth, viewportWidth - popoverWidth - 12));
  return {
    top: Math.max(12, rect.bottom + 8),
    left,
  };
}

function TsunamiColorPresetPicker({
  item,
  colorPresets,
  setColorOverrides,
  openPicker,
  setOpenPicker,
}) {
  const triggerRef = useRef(null);
  const activeColor = normalizeHexColor(item.color) || TSUNAMI_NEUTRAL_FILL;
  const isOpen = openPicker?.entityIdentity === item.entityIdentity;

  function openPalette() {
    setOpenPicker({
      entityIdentity: item.entityIdentity,
      ...getColorPopoverPosition(triggerRef.current),
    });
  }

  function closePalette() {
    setOpenPicker((current) =>
      current?.entityIdentity === item.entityIdentity ? null : current
    );
  }

  return (
    <div className="tsunami-color-picker">
      <button
        ref={triggerRef}
        type="button"
        className="tsunami-color-trigger"
        aria-label={`Upravit barvu: ${item.label}`}
        aria-expanded={isOpen}
        onClick={openPalette}
      >
        <span className="tsunami-color-button-swatch" style={{ background: activeColor }} aria-hidden="true" />
      </button>
      {isOpen ? (
        <div
          className="tsunami-color-menu"
          style={{ top: openPicker.top, left: openPicker.left }}
          role="dialog"
          aria-label={`Vyberte barvu pro ${item.label}`}
        >
          <div className="tsunami-color-menu-header">
            <div className="tsunami-color-menu-title">Vyberte barvu</div>
            <button
              type="button"
              className="tsunami-color-close"
              aria-label="Zavřít výběr barvy"
              onClick={closePalette}
            >
              ×
            </button>
          </div>
          <div className="tsunami-color-grid">
            {colorPresets.map((preset) => {
              const color = normalizeHexColor(preset.hex);
              if (!color) return null;
              const isActive = color === activeColor;
              return (
                <button
                  key={`${item.entityIdentity}-${color}`}
                  type="button"
                  className={`tsunami-color-preset${isActive ? " is-active" : ""}`}
                  style={{ background: color }}
                  aria-label={`${preset.name}: ${item.label}`}
                  aria-pressed={isActive}
                  title={preset.name}
                  onClick={() => {
                    setColorOverrides((current) => ({
                      ...sanitizeTsunamiColorOverrides(current),
                      [item.entityIdentity]: color,
                    }));
                  }}
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TsunamiLegend({
  legendItems,
  colorPresets,
  setColorOverrides,
  selectedRank,
  openPicker,
  setOpenPicker,
}) {
  return (
    <section className="tsunami-legend" aria-label="Legenda">
      <div className="tsunami-panel-title">Legenda ({selectedRank}. pořadí)</div>
      <div className="tsunami-legend-list">
        {legendItems.map((item) => {
          const colorValue = normalizeHexColor(item.color) || TSUNAMI_NEUTRAL_FILL;
          const isNeutral = !item.entityIdentity;
          return (
            <div key={item.key} className={`tsunami-legend-row${isNeutral ? " is-neutral" : ""}`}>
              <span className="tsunami-legend-swatch" style={{ background: colorValue }} aria-hidden="true" />
              <span className="tsunami-legend-label" title={item.label}>{item.label}</span>
              {item.entityIdentity ? (
                <TsunamiColorPresetPicker
                  item={item}
                  colorPresets={colorPresets}
                  setColorOverrides={setColorOverrides}
                  openPicker={openPicker}
                  setOpenPicker={setOpenPicker}
                />
              ) : (
                <span className="tsunami-neutral-badge">neutrální</span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function TsunamiPage({ areaId }) {
  const storedSettings = useMemo(() => readStoredTsunamiSettings(), []);
  const [elections, setElections] = useState([]);
  const [electionsError, setElectionsError] = useState("");
  const [isLoadingElections, setIsLoadingElections] = useState(false);
  const [electionId, setElectionId] = useState("");
  const [selectedRank, setSelectedRank] = useState(() => normalizeTsunamiRank(storedSettings.selectedRank));
  const [colorOverrides, setColorOverrides] = useState(() =>
    sanitizeTsunamiColorOverrides(storedSettings.colorOverrides)
  );
  const [openPicker, setOpenPicker] = useState(null);
  const [hoveredPrecinct, setHoveredPrecinct] = useState(null);
  const [pinnedPrecinct, setPinnedPrecinct] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const mapRef = useRef(null);
  const layerIndexRef = useRef(new Map());
  const initialViewport = useMemo(() => getInitialMapView("tsunami", areaId), [areaId]);
  const hasStoredInitialViewportRef = useRef(Boolean(readStoredViewport("tsunami", areaId)));
  const [fitRequestKey, setFitRequestKey] = useState(() =>
    hasStoredInitialViewportRef.current ? "" : makeAutoFitRequestKey("tsunami", areaId, "initial")
  );
  const prevAreaIdRef = useRef(areaId);

  useEffect(() => {
    writeStoredTsunamiSettings({
      selectedRank,
      colorOverrides: sanitizeTsunamiColorOverrides(colorOverrides),
    });
  }, [colorOverrides, selectedRank]);

  useEffect(() => {
    if (!areaId) {
      setElections([]);
      setElectionId("");
      return undefined;
    }

    const ac = new AbortController();
    setIsLoadingElections(true);
    setElectionsError("");
    setElections([]);
    setElectionId("");

    (async () => {
      try {
        const response = await fetch(`/api/areas/${encodeURIComponent(String(areaId))}/elections`, {
          credentials: "include",
          signal: ac.signal,
        });
        if (!response.ok) throw createHttpError(response.status, "enabled elections");
        const payload = await response.json();
        if (ac.signal.aborted) return;
        const nextElections = sortElectionsNewestFirst(Array.isArray(payload) ? payload : []);
        const storedSelection = readElectionSelectionContext(areaId);
        const fallbackElectionId = nextElections[0] && nextElections[0].id ? String(nextElections[0].id) : "";
        setElections(nextElections);
        setElectionId((current) =>
          pickValidElectionId(
            nextElections,
            [current, storedSelection.tsunamiElectionId, storedSelection.analyticsElectionId, storedSelection.lastElectionId],
            fallbackElectionId
          )
        );
      } catch (error) {
        if (error?.name === "AbortError") return;
        setElectionsError(error && Number(error.status) === 403 ? "Tato oblast není pro váš účet dostupná." : "Nepodařilo se načíst povolené volby.");
      } finally {
        if (!ac.signal.aborted) setIsLoadingElections(false);
      }
    })();

    return () => ac.abort();
  }, [areaId]);

  useEffect(() => {
    if (!areaId || !electionId) return;
    writeElectionSelectionContext(areaId, {
      tsunamiElectionId: electionId,
      lastElectionId: electionId,
    });
  }, [areaId, electionId]);

  useEffect(() => {
    if (prevAreaIdRef.current === areaId) return;
    prevAreaIdRef.current = areaId;
    clearStoredViewport("tsunami", areaId);
    setFitRequestKey(makeAutoFitRequestKey("tsunami", areaId, "area"));
  }, [areaId]);

  const resetMapToArea = useCallback(() => {
    setHoveredPrecinct(null);
    setPinnedPrecinct(null);
    setTooltip(null);
    clearStoredViewport("tsunami", areaId);
    setFitRequestKey(makeAutoFitRequestKey("tsunami", areaId, "reset"));
  }, [areaId]);

  useEffect(() => {
    layerIndexRef.current = new Map();
    setHoveredPrecinct(null);
    setPinnedPrecinct(null);
    setTooltip(null);
    if (electionId) setFitRequestKey(makeAutoFitRequestKey("tsunami", areaId, `election-${electionId}`));
  }, [areaId, electionId]);

  const data = useElectionData(electionId, areaId);
  const features = useMemo(
    () => (Array.isArray(data.districtsGeoJson?.features) ? data.districtsGeoJson.features : []),
    [data.districtsGeoJson]
  );
  const featureCount = features.length;
  const subjectLabel = getSubjectLabel(electionId);
  const paletteColors = useMemo(() => getTsunamiPaletteColors(BRAND_MAP_COLOR_PRESETS), []);
  const rankByPrecinct = useMemo(
    () => buildTsunamiRankByPrecinct(data.results, selectedRank),
    [data.results, selectedRank]
  );
  const rankEntries = useMemo(() => Array.from(rankByPrecinct.values()), [rankByPrecinct]);
  const allEntityEntries = useMemo(() => {
    const entities = new Map();
    for (const row of Array.isArray(data.results) ? data.results : []) {
      for (const result of Array.isArray(row && row.results) ? row.results : []) {
        const entityIdentity = getTsunamiEntityIdentity(result);
        if (!entityIdentity || entities.has(entityIdentity)) continue;
        entities.set(entityIdentity, {
          entityIdentity,
          entityName: getTsunamiEntityName(result) || entityIdentity,
        });
      }
    }
    return Array.from(entities.values());
  }, [data.results]);
  const colorMap = useMemo(
    () => buildTsunamiEntityColorMap(allEntityEntries, colorOverrides, paletteColors),
    [allEntityEntries, colorOverrides, paletteColors]
  );
  const statsByPrecinct = useMemo(() => buildStatsByPrecinct(data.stats), [data.stats]);

  const rowsByPrecinct = useMemo(() => {
    const next = new Map();
    for (const feature of features) {
      const precinctId = getFeaturePrecinctId(feature);
      if (!precinctId) continue;
      next.set(precinctId, {
        precinctId,
        precinctLabel: getFeaturePrecinctLabel(feature, electionId),
        rankEntry: rankByPrecinct.get(precinctId) || null,
        statsInfo: getStatsForOkrsek(statsByPrecinct, precinctId, electionId),
      });
    }
    return next;
  }, [electionId, features, rankByPrecinct, statsByPrecinct]);

  const tooltipRow = useMemo(() => {
    if (!tooltip?.precinctId) return null;
    return rowsByPrecinct.get(String(tooltip.precinctId)) || null;
  }, [rowsByPrecinct, tooltip]);

  const legendItems = useMemo(() => {
    const visibleIdentities = new Set(rankEntries.map((entry) => entry.entityIdentity).filter(Boolean));
    const items = Array.from(visibleIdentities)
      .map((entityIdentity) => colorMap.get(entityIdentity))
      .filter(Boolean)
      .sort((a, b) => {
        const byName = String(a.entityName || "").localeCompare(String(b.entityName || ""), "cs", { sensitivity: "base" });
        if (byName !== 0) return byName;
        return String(a.entityIdentity || "").localeCompare(String(b.entityIdentity || ""), "cs", { sensitivity: "base" });
      })
      .map((item) => ({
        key: item.entityIdentity,
        entityIdentity: item.entityIdentity,
        label: item.entityName,
        color: item.color,
      }));
    items.push({
      key: "neutral",
      entityIdentity: "",
      label: "Bez dat / pořadí mimo rozsah",
      color: TSUNAMI_NEUTRAL_FILL,
    });
    return items;
  }, [colorMap, rankEntries]);
  const electionLabel = getElectionLabel(elections.find((item) => String(item.id) === String(electionId)) || { id: electionId });
  const tsunamiColorMapping = useMemo(() => {
    const next = {};
    for (const [key, value] of colorMap.entries()) {
      next[key] = {
        label: value.entityName || key,
        color: value.color,
      };
    }
    return next;
  }, [colorMap]);

  const buildTsunamiReportSnapshot = useCallback(
    () => ({
      kind: REPORT_ITEM_TYPES.TSUNAMI_MAP,
      sourceModule: REPORT_SOURCE_MODULES.TSUNAMI,
      areaId,
      electionId,
      electionLabel,
      selectedRank,
      rankLabel: `${selectedRank}. pořadí`,
      selectedEntity: `${selectedRank}. pořadí`,
      colorMapping: tsunamiColorMapping,
      legend: mapLegendToSnapshot(legendItems),
      viewport: captureLeafletViewport(mapRef.current) || initialViewport,
      capturedAt: new Date().toISOString(),
    }),
    [areaId, electionId, electionLabel, initialViewport, legendItems, selectedRank, tsunamiColorMapping]
  );

  const styleFeature = useCallback(
    (feature) => {
      const precinctId = getFeaturePrecinctId(feature);
      const isHovered = hoveredPrecinct != null && String(hoveredPrecinct) === precinctId;
      const isSelected = pinnedPrecinct != null && String(pinnedPrecinct) === precinctId;
      const baseStyle = getElectionMapFeatureStyle({
        fillColor: getTsunamiFillColor(rankByPrecinct.get(precinctId), colorMap, TSUNAMI_NEUTRAL_FILL),
        noDataFillColor: TSUNAMI_NEUTRAL_FILL,
        isHovered,
        isSelected,
        clearOnFocus: true,
      });
      if (isSelected || isHovered) {
        return {
          ...baseStyle,
          opacity: 1,
          weight: isSelected ? Math.max(baseStyle.weight || 0, 4.8) : Math.max(baseStyle.weight || 0, 3.8),
        };
      }
      return {
        ...baseStyle,
        opacity: 0.86,
        weight: Math.max(baseStyle.weight || 0, 1.35),
      };
    },
    [colorMap, hoveredPrecinct, pinnedPrecinct, rankByPrecinct]
  );

  useEffect(() => {
    for (const layer of layerIndexRef.current.values()) {
      if (!layer || typeof layer.setStyle !== "function") continue;
      try {
        layer.setStyle(styleFeature(layer.feature));
      } catch (_) {
        // Ignore stale Leaflet layers during election switches.
      }
    }
  }, [styleFeature]);

  const hideTooltip = useCallback((source) => {
    setTooltip((current) => {
      if (!current) return null;
      if (source && current.source !== source) return current;
      return null;
    });
  }, []);

  const showTooltipAtEvent = useCallback((source, precinctId, event) => {
    const originalEvent = event && event.originalEvent ? event.originalEvent : event;
    const clientX = Number(originalEvent && originalEvent.clientX);
    const clientY = Number(originalEvent && originalEvent.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1440;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 900;
    setTooltip({
      source,
      precinctId: String(precinctId || ""),
      x: Math.max(12, Math.min(clientX + 18, viewportWidth - 330)),
      y: Math.max(12, Math.min(clientY + 18, viewportHeight - 190)),
    });
  }, []);

  const togglePinnedPrecinct = useCallback((precinctId) => {
    const nextPrecinct = String(precinctId || "");
    setPinnedPrecinct((current) => (String(current || "") === nextPrecinct ? null : nextPrecinct));
  }, []);

  const onEachFeature = useCallback(
    (feature, layer) => {
      const precinctId = getFeaturePrecinctId(feature);
      if (!precinctId) return;
      layerIndexRef.current.set(precinctId, layer);
      layer.on({
        mouseover: (event) => {
          setHoveredPrecinct(precinctId);
          showTooltipAtEvent("map", precinctId, event);
        },
        mousemove: (event) => showTooltipAtEvent("map", precinctId, event),
        mouseout: () => {
          setHoveredPrecinct((current) => (String(current || "") === precinctId ? null : current));
          hideTooltip("map");
        },
        click: () => togglePinnedPrecinct(precinctId),
      });
    },
    [hideTooltip, showTooltipAtEvent, togglePinnedPrecinct]
  );

  const loadingText = [
    isLoadingElections ? "volby" : "",
    data.isLoadingDistricts ? "okrsky" : "",
    data.isLoadingResults ? "výsledky" : "",
    data.isLoadingStats ? "statistiky" : "",
  ].filter(Boolean).join(" + ");
  const geometryBlockingError =
    data.districtsError || (!data.isLoadingDistricts && electionId && featureCount === 0 ? "Geometrie okrsků není k dispozici." : "");
  const softWarnings = [electionsError, data.resultsError, data.statsError].filter(Boolean);
  const mapKey = `tsunami-${areaId || "none"}`;
  const geoJsonKey = `${electionId || "none"}-${featureCount}`;

  return (
    <main className="tsunami-page">
      <style>{`
        .tsunami-page {
          position: relative;
          width: 100%;
          min-height: calc(100vh - var(--app-shell-topbar-offset, 0px) - 40px);
          padding: 20px 0 36px;
          box-sizing: border-box;
          color: #0f172a;
          overflow: visible;
        }
        .tsunami-card {
          width: min(1320px, calc(100% - 24px));
          margin: 0 auto;
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #ffffff;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
          padding: 16px;
          box-sizing: border-box;
          display: grid;
          gap: 14px;
        }
        .tsunami-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .tsunami-map-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(276px, 330px);
          gap: 14px;
          align-items: stretch;
        }
        .tsunami-map-frame {
          position: relative;
          min-width: 0;
          overflow: hidden;
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #f8fafc;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72), 0 1px 2px rgba(15, 23, 42, 0.04);
        }
        .tsunami-map-frame > div {
          height: 100%;
          border: 0 !important;
          border-radius: 0 !important;
        }
        .tsunami-controls {
          display: grid;
          gap: 12px;
        }
        .tsunami-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .tsunami-title {
          margin: 0;
          font-size: 1.15rem;
          line-height: 1.1;
          font-weight: 900;
          color: ${BRAND_COLORS.primaryDarkBlue};
        }
        .tsunami-control-grid {
          display: grid;
          grid-template-columns: minmax(190px, 0.82fr) minmax(280px, 1.45fr) minmax(208px, auto);
          gap: 12px;
          align-items: end;
        }
        .tsunami-control-stack {
          display: grid;
          gap: 5px;
          min-width: 0;
        }
        .tsunami-label,
        .tsunami-panel-title {
          font-size: 0.74rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #475569;
        }
        .tsunami-select {
          width: 100%;
          min-height: 34px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: white;
          color: #0f172a;
          font-size: 0.86rem;
          font-weight: 700;
          padding: 7px 10px;
        }
        .tsunami-control-note,
        .tsunami-control-error {
          font-size: 0.76rem;
          font-weight: 700;
        }
        .tsunami-control-note {
          color: #475569;
        }
        .tsunami-control-error {
          color: #991b1b;
        }
        .tsunami-rank-group {
          display: inline-grid;
          grid-template-columns: repeat(5, 38px);
          gap: 6px;
        }
        .tsunami-rank-button {
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: white;
          color: #1e293b;
          cursor: pointer;
          font-weight: 800;
        }
        .tsunami-rank-button {
          width: 38px;
          height: 38px;
        }
        .tsunami-rank-button.is-active {
          border-color: ${BRAND_COLORS.primaryDarkBlue};
          background: ${BRAND_COLORS.primaryDarkBlue};
          color: white;
        }
        .tsunami-status {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          font-size: 0.82rem;
        }
        .tsunami-status span {
          border-radius: 999px;
          padding: 5px 8px;
          background: #eff6ff;
          color: #1e3a8a;
          font-weight: 700;
        }
        .tsunami-status .is-error {
          background: #fef2f2;
          color: #991b1b;
        }
        .tsunami-legend {
          min-width: 0;
          max-height: 620px;
          overflow: auto;
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #fbfdff;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
          padding: 14px;
          display: grid;
          gap: 12px;
          align-content: start;
        }
        .tsunami-legend-list {
          display: grid;
          gap: 9px;
        }
        .tsunami-legend-row {
          display: grid;
          grid-template-columns: 18px minmax(0, 1fr) auto;
          gap: 10px;
          align-items: start;
          min-height: 34px;
          padding: 7px 8px;
          border: 1px solid transparent;
          border-radius: 8px;
          background: #ffffff;
        }
        .tsunami-legend-row.is-neutral {
          background: #f1f5f9;
          border-color: #e2e8f0;
          color: #64748b;
        }
        .tsunami-legend-swatch {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid rgba(15, 23, 42, 0.18);
          margin-top: 2px;
        }
        .tsunami-legend-label {
          overflow-wrap: anywhere;
          word-break: normal;
          font-size: 0.86rem;
          font-weight: 700;
          line-height: 1.25;
        }
        .tsunami-color-picker {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        .tsunami-color-trigger,
        .tsunami-color-close {
          width: 34px;
          height: 30px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          cursor: pointer;
          display: grid;
          place-items: center;
          color: #1e293b;
          font-weight: 900;
        }
        .tsunami-color-button-swatch {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1px solid rgba(15, 23, 42, 0.18);
        }
        .tsunami-neutral-badge {
          border-radius: 999px;
          background: #e2e8f0;
          color: #475569;
          padding: 3px 7px;
          font-size: 0.68rem;
          font-weight: 800;
          line-height: 1.2;
          white-space: nowrap;
        }
        .tsunami-color-menu {
          position: fixed;
          z-index: 2200;
          width: 236px;
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #ffffff;
          box-shadow: 0 18px 38px rgba(15, 23, 42, 0.18);
          padding: 12px;
          display: grid;
          gap: 12px;
        }
        .tsunami-color-menu-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .tsunami-color-menu-title {
          font-size: 0.74rem;
          font-weight: 800;
          color: #334155;
        }
        .tsunami-color-close {
          width: 28px;
          height: 28px;
          font-size: 1rem;
        }
        .tsunami-color-grid {
          display: grid;
          grid-template-columns: repeat(6, 26px);
          gap: 8px;
        }
        .tsunami-color-preset {
          width: 26px;
          height: 26px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          border-radius: 6px;
          cursor: pointer;
        }
        .tsunami-color-preset.is-active {
          outline: 2px solid ${BRAND_COLORS.primaryDarkBlue};
          outline-offset: 2px;
        }
        .tsunami-blocking-error {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 620;
          transform: translate(-50%, -50%);
          width: min(460px, calc(100vw - 36px));
          padding: 16px;
          text-align: center;
          font-weight: 800;
          color: #991b1b;
        }
        .tsunami-tooltip {
          position: fixed;
          z-index: 1800;
          width: 300px;
          border: 1px solid rgba(15, 23, 42, 0.14);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
          padding: 10px 12px;
          pointer-events: none;
          color: #0f172a;
        }
        .tsunami-tooltip-title {
          font-weight: 900;
          margin-bottom: 7px;
        }
        .tsunami-tooltip-row,
        .tsunami-tooltip-grid {
          display: grid;
          gap: 5px 10px;
          font-size: 0.84rem;
        }
        .tsunami-tooltip-row {
          grid-template-columns: 74px minmax(0, 1fr);
          margin-bottom: 8px;
        }
        .tsunami-tooltip-grid {
          grid-template-columns: 74px minmax(0, 1fr);
        }
        .tsunami-tooltip span {
          color: #475569;
        }
        @media (max-width: 860px) {
          .tsunami-page {
            padding-top: 14px;
          }
          .tsunami-card {
            padding: 12px;
            border-radius: 8px;
          }
          .tsunami-card-header,
          .tsunami-control-grid,
          .tsunami-map-layout {
            grid-template-columns: minmax(0, 1fr);
          }
          .tsunami-card-header {
            display: grid;
          }
          .tsunami-legend {
            max-height: 34vh;
          }
        }
        @media (min-width: 861px) and (max-width: 1180px) {
          .tsunami-card {
            width: min(1180px, calc(100% - 20px));
          }
          .tsunami-map-layout {
            grid-template-columns: minmax(0, 1fr) minmax(248px, 292px);
          }
          .tsunami-control-grid {
            grid-template-columns: minmax(170px, 0.85fr) minmax(250px, 1.2fr) minmax(208px, auto);
          }
        }
      `}</style>

      <section className="tsunami-card" aria-label="Tsunami">
        <div className="tsunami-card-header">
          <AddToReportButton
            type={REPORT_ITEM_TYPES.TSUNAMI_MAP}
            sourceModule={REPORT_SOURCE_MODULES.TSUNAMI}
            defaultOrientation={REPORT_ORIENTATIONS.LANDSCAPE}
            defaultTitle={buildDefaultReportTitle([electionLabel, `${selectedRank}. pořadí`, "Tsunami"])}
            buildSnapshot={buildTsunamiReportSnapshot}
          />
        </div>

        <section className="tsunami-controls" aria-label="Tsunami controls">
          <div className="tsunami-control-grid">
            <TsunamiAreaSelect />
            <label className="tsunami-control-stack">
              <span className="tsunami-label">Volby</span>
              <select
                className="tsunami-select"
                value={electionId}
                onChange={(event) => setElectionId(event.target.value)}
                disabled={isLoadingElections || elections.length === 0}
              >
                {elections.length === 0 ? <option value="">Žádné povolené volby</option> : null}
                {elections.map((election) => (
                  <option key={String(election.id)} value={String(election.id)}>
                    {getElectionLabel(election)}
                  </option>
                ))}
              </select>
            </label>
            <div className="tsunami-control-stack">
              <span className="tsunami-label">Pořadí</span>
              <div className="tsunami-rank-group" role="group" aria-label="Pořadí">
                {TSUNAMI_RANKS.map((rank) => (
                  <button
                    key={rank}
                    type="button"
                    className={`tsunami-rank-button${selectedRank === rank ? " is-active" : ""}`}
                    onClick={() => setSelectedRank(rank)}
                    aria-pressed={selectedRank === rank}
                  >
                    {rank}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {loadingText || softWarnings.length > 0 ? (
            <div className="tsunami-status" role={softWarnings.length > 0 ? "alert" : "status"}>
              {loadingText ? <span>Načítám: {loadingText}</span> : null}
              {softWarnings.map((warning) => (
                <span key={warning} className="is-error">{warning}</span>
              ))}
            </div>
          ) : null}
        </section>

        <div className="tsunami-map-layout">
          <div className="tsunami-map-frame">
            <MapPane
              mapKey={mapKey}
              geoJsonKey={geoJsonKey}
              districtsGeoJson={data.districtsGeoJson}
              styleFeature={styleFeature}
              onEachFeature={onEachFeature}
              mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
              height="clamp(460px, 58vh, 720px)"
              scrollWheelZoom={false}
              initialView={initialViewport}
              fitGeoJsonBounds
              fitBoundsRequestKey={fitRequestKey}
              fitBoundsPadding={[28, 28]}
              fitBoundsMaxZoom={14}
              onMapReady={(map) => {
                mapRef.current = map;
              }}
              onViewChange={(center, zoom) => {
                writeStoredViewport("tsunami", areaId, { center, zoom });
              }}
              onMouseLeave={() => {
                setHoveredPrecinct(null);
                hideTooltip("map");
              }}
              onInteractionStart={() => {
                setHoveredPrecinct(null);
                hideTooltip("map");
              }}
              onResetView={resetMapToArea}
            />
            {geometryBlockingError ? (
              <div className="tsunami-blocking-error" role="alert">
                {geometryBlockingError}
              </div>
            ) : null}
          </div>

          <TsunamiLegend
            legendItems={legendItems}
            colorPresets={BRAND_MAP_COLOR_PRESETS}
            setColorOverrides={setColorOverrides}
            selectedRank={selectedRank}
            openPicker={openPicker}
            setOpenPicker={setOpenPicker}
          />
        </div>
      </section>

      <TsunamiTooltip tooltip={tooltip} row={tooltipRow} subjectLabel={subjectLabel} selectedRank={selectedRank} />
    </main>
  );
}
